import { PortableText } from '@portabletext/react'
import type { InferGetServerSidePropsType, GetServerSideProps } from 'next'
import { useLiveQuery } from '~/lib/sanity-live-query'
import { getGlobalData } from '../../utils/global-data';
import Footer from '../../components/Footer';
import Layout from '../../components/Layout';
import SEO from '../../components/SEO';
import StickyNavbar from '../../components/StickyNavbar';
import HorizontalLine from '../../components/HorizontalLine';
import SliderTimber from '../../components/Sliders/SliderTimber';
import SliderCategory from '../../components/Sliders/SliderCategory';

import { readToken } from '~/lib/sanity.api'
import { getClient } from '~/lib/sanity.client'
import {
  getInspiration,
  getSiteConfig,
  inspirationBySlugQuery,  
  siteConfigQuery,
} from '~/lib/sanity.queries'
import { type Inspiration, SiteConfig } from '~/lib/sanity.interfaces'
import type { SharedPageProps } from '~/pages/_app'
import CarouselTitle from "~/components/CarouselTitle/CarouselTitle";
import Link from 'next/link';

  interface Query {
    [key: string]: string
  }
  
  export const getServerSideProps: GetServerSideProps<
    SharedPageProps & {
      inspiration: Inspiration,
      siteConfig: SiteConfig,
    },
    Query
  > = async ({ draftMode = false, params = {} }) => {
    const client = getClient(draftMode ? { token: readToken } : undefined)

    const inspiration = await getInspiration(client, params.slug)
    const siteConfig = await getSiteConfig(client)
    const globalData = getGlobalData()
  
    if (!inspiration) {
      return {
        notFound: true,
      }
    }
  
    return {
      props: {
        globalData,
        draftMode,
        token: draftMode ? readToken : '',
        inspiration,
        siteConfig,
      },
    }
  }

export default function InspirationPage(
  props: InferGetServerSidePropsType<typeof getServerSideProps>,
  globalData
) {
  const [inspiration] = useLiveQuery(props.draftMode, props.inspiration, inspirationBySlugQuery, {
    slug: props.inspiration.slug.current,
  })
  const [siteConfig] = useLiveQuery(props.draftMode, props.siteConfig, siteConfigQuery)

  const slides = []
  if(inspiration.mainCarousel) {
    inspiration.mainCarousel.map((item) => {
      slides.push({
        _id: item._id,
        image: item,
        title: item.title
      });
    })
  }

  return (
    <Layout>
        <SEO title={inspiration.seoTitle} description={inspiration.seoDescription} keywords={inspiration.seoKeywords} />
        <StickyNavbar siteConfig={siteConfig} />
        <main className="w-full bg-background nav-offset py-4 lg:py-8">     
            <div className='mx-2 md:mx-10'>
                <div className='mb-10'>
                  <CarouselTitle slides={slides} />
                </div>
                <h1 className="text-3xl lg:text-6xl font-bold text-left mb-12 mr-10">
                  {inspiration.title}
                </h1>
                <div className='contentBlock text-left text-lg'>
                  <PortableText value={inspiration.body} />
                </div>
                <HorizontalLine />
                {inspiration.selectedTimbers && ( 
                <>
                  <div className="text-right">
                      <h3 className="text-4xl font-semibold text-primary mb-10">Timbers Used</h3>
                  </div>                 
                  <SliderTimber timbers={inspiration.selectedTimbers} />
                  <div className='flex justify-end'>
                    <Link className="mt-2 font-bold underline" href="/timbers">See all Timber Species</Link>
                  </div>
                  <HorizontalLine />
                </>
                )}
                
                {inspiration.bodysecond && (
                  <>
                  <div className='contentBlock text-left text-lg'>
                    <PortableText value={inspiration.bodysecond} />
                  </div>
                  <HorizontalLine />
                  </>
                )}
                
            </div> 
            {inspiration.selectedSystems && (  
            <div className='mx-2 md:mx-10'>
                <div className="text-right">
                    <h3 className="text-4xl font-semibold text-primary mb-10">Timber Systems</h3>
                </div> 
                <SliderCategory items={inspiration.selectedSystems} prefix="/systems/" />
                <HorizontalLine />
            </div>  
             )} 
             {inspiration.selectedProducts && (  
            <div className='mx-2 md:mx-10'>
                <div className="text-right">
                    <h3 className="text-4xl font-semibold text-primary mb-10">Products</h3>
                </div> 
                <SliderCategory items={inspiration.selectedProducts} prefix="/systems/" />
                <HorizontalLine />
            </div>  
             )}  
            {inspiration.selectedInspirations && (  
            <div className='mx-2 md:mx-10'>
                <div className="text-right">
                    <h3 className="text-4xl font-semibold text-primary mb-10">More Inspiration</h3>
                </div> 
                <SliderCategory items={inspiration.selectedInspirations} prefix="/inspiration/" />
                <HorizontalLine />
            </div>  
             )} 
            </main>        
        <Footer siteConfig={siteConfig} />      
    </Layout>
  );
}

